import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl, storageBaseUrl } from "store/baseUrls";
import { toAuth } from "utils/text";
import { xApiKey } from "store/apiKeys";
import type { RootState } from "..";
import type { User } from "store/user/user.model";
import type { Plan } from "./current-user.model";
import type { Result } from "store/api.type";
import type { Genre, HomeGenre } from "store/genre/genre.model";
import type { Track } from "store/track/track.model";
import type { Playlist } from "store/playlist/playlist.model";
import type { Artist } from "store/artist/artist.model";
import type { Match } from "store/match/match.model";
import type { Announcement } from "store/announcement/announcement.model";
import type { Chat, Message } from "store/chat/chat.model";

interface SendOtpRequest {
  phone: string;
  mode?: "dev";
}

interface SendOtpResponse {
  status: boolean;
  otp?: string;
}

interface VerifyOtpRequest {
  phone: string;
  code: string;
}

interface VerifyOtpResponse {
  status: boolean;
  key: string;
  user: Partial<User>;
}

interface CreateUserRequest {
  key: string;
  firstName: string;
  lastName: string;
  gender: "Man" | "Woman";
  userName: string;
  phoneNumber: string;
  password: string;
  passwordConfirm: string;
}

interface LoginRequest {
  userName: string;
  password: string;
}

interface LoginResponse {
  token: string;
  user: User;
}
export interface UploadFileRequest {
  name: string;
  type: string;
  role: "Attachment";
  relatedType: "User";
  file: string | null;
  field: "avatar" | "gallery";
}

export interface UploadFileResponse {
  id: string;
  name: string;
  type: string;
  role: "Attachment";
  relatedType: "User";
  file: string | null;
  field: "avatar" | "gallery";
  avatarId: string;
  avatarName: string;
}

export const MESSAGE_PAGE_SIZE = 12;

export const currentUserApi = createApi({
  reducerPath: "currentUserApi",
  tagTypes: [
    "currentUser",
    "userGenres",
    "playedTracks",
    "likedTracks",
    "likedArtists",
    "likedPlaylists",
    "editablePlaylists",
    "editablePlaylistTracks",
    "followRequests",
    "user",
    "friends",
    "unreadNotificationsCount",
    "messages",
    "paginatedMessages",
    "chats",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const {
        currentUser: { auth },
        _persist: { rehydrated },
      } = (getState() as RootState).persistedReducer;
      if (headers.get("Arvano-Authorization") === "placeholder") {
        if (rehydrated) {
          if (auth) {
            headers.set("Arvano-Authorization", auth);
          } else {
            window.location.pathname = "/welcome";
          }
        } else {
          console.error("unexpected condition in Arvano-Authorization");
        }
      }
      headers.set("Accept", "application/json");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    sendOtp: builder.mutation<SendOtpResponse, SendOtpRequest>({
      query: (body) => ({ url: "/v1/opt/send", method: "POST", body }),
    }),
    verifyOtp: builder.mutation<VerifyOtpResponse, VerifyOtpRequest>({
      query: (body) => ({
        url: "/v1/opt/verify",
        method: "POST",
        body,
      }),
    }),
    createUser: builder.mutation<Partial<User>, CreateUserRequest>({
      query: (body) => ({
        url: "/v1/signup",
        method: "POST",
        body,
      }),
    }),
    uploadFile: builder.mutation<UploadFileResponse, UploadFileRequest>({
      query: (body) => ({
        url: `${storageBaseUrl}/v1/Attachment`,
        method: "POST",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
    }),
    assignAvatar: builder.mutation<
      User,
      { userId: string; body: { avatarId: string; avatarName: string } }
    >({
      query: ({ userId, body }) => ({
        url: `/v1/User/${userId}`,
        method: "PUT",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["currentUser"],
    }),
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: ({ userName, password }) => ({
        url: `/v1/App/user`,
        method: "GET",
        headers: [["Arvano-Authorization", toAuth(userName, password)]],
      }),
    }),
    getPlans: builder.query<Plan[], void>({
      query: () => ({
        url: `/v1/Plan?where[0][type]=isFalse&where[0][attribute]=disable&orderBy=off`,
        method: "GET",
        headers: [["X-Api-Key", xApiKey]],
      }),
      transformResponse: (data: Result<Plan>) => data.list,
    }),
    getUserGenres: builder.query<Genre[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/user/${userId}/genres`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Genre>) => data.list,
      providesTags: ["userGenres"],
    }),
    setUserGenres: builder.mutation<
      void,
      { userId: string; body: { ids: string[] } }
    >({
      query: ({ userId, body }) => ({
        url: `/v1/user/${userId}/genres`,
        method: "POST",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["userGenres"],
    }),
    removeFromUserGenres: builder.mutation<
      void,
      { userId: string; body: { ids: string[] } }
    >({
      query: ({ userId, body }) => ({
        url: `/v1/user/${userId}/genres`,
        method: "DELETE",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["userGenres"],
    }),
    editUser: builder.mutation<User, { userId: string; body: Partial<User> }>({
      query: ({ userId, body }) => ({
        url: `/v1/User/${userId}`,
        method: "PUT",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["currentUser"],
    }),
    resetPassword: builder.mutation<Partial<User>, Partial<User>>({
      query: (body) => ({
        url: `/v1/reset/password`,
        method: "POST",
        body,
      }),
    }),
    editPassword: builder.mutation<
      void,
      { body: { currentPassword: string; password: string } }
    >({
      query: ({ body }) => ({
        url: `/v1/User/changePassword`,
        method: "POST",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
    }),
    getCurrentUser: builder.query<User, { currentUserId: string }>({
      query: ({ currentUserId }) => ({
        url: `/v1/user/${currentUserId}`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      providesTags: ["currentUser"],
    }),
    getUserHomeGenres: builder.query<HomeGenre[], void>({
      query: () => ({
        url: `/v1/App/home/user/genre`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<HomeGenre>) => data.list,
      providesTags: ["userGenres"],
      keepUnusedDataFor: 3600,
    }),

    addToPlayedTracks: builder.mutation<boolean, { track: Track }>({
      query: ({ track }) => ({
        url: `/v1/Track/play/${track.id}`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, {}) => [
        { type: "playedTracks", id: `0-20` },
        { type: "playedTracks", id: `0-10` },
      ],
      async onQueryStarted({ track }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getPlayedTracks",
            { offset: 0, limit: 20 },
            (draft) => {
              // TODO remove duplicate tracks
              draft.splice(0, 0, track);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getPlayedTracks: builder.query<Track[], { offset: number; limit: number }>({
      query: ({ offset, limit }) => ({
        url: `/v1/User/track/recently?offset=${offset}&maxSize=${limit}`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Track>) => data.list,
      providesTags: (result, error, { offset, limit }) => [
        { type: "playedTracks", id: `${offset}-${limit}` },
      ],
    }),

    addToLikedTracks: builder.mutation<
      boolean,
      { userId: string; body: { ids: string[] }; tracks: Track[] }
    >({
      query: ({ userId, body }) => ({
        url: `/v1/User/${userId}/tracks`,
        method: "POST",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "likedTracks", id: userId },
      ],
      async onQueryStarted({ userId, tracks }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getLikedTracks",
            { userId },
            (draft) => {
              draft.splice(0, 0, ...tracks);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    removeFromLikedTracks: builder.mutation<
      boolean,
      { userId: string; body: { ids: string[] } }
    >({
      query: ({ userId, body }) => ({
        url: `/v1/User/${userId}/tracks`,
        method: "DELETE",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "likedTracks", id: userId },
      ],
      async onQueryStarted(
        { userId, body: { ids } },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getLikedTracks",
            { userId },
            (draft) => {
              for (let idIndex = 0; idIndex < ids.length; idIndex++) {
                draft.splice(
                  draft.findIndex(
                    (likedTrack) => ids[idIndex] === likedTrack.id
                  ),
                  1
                );
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getLikedTracks: builder.query<Track[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/User/${userId}/tracks`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Track>) => data.list,
      providesTags: (result, error, { userId }) => [
        { type: "likedTracks", id: userId },
      ],
    }),

    addToLikedArtists: builder.mutation<
      boolean,
      { userId: string; body: { ids: string[] }; artists: Artist[] }
    >({
      query: ({ userId, body }) => ({
        url: `/v1/User/${userId}/artists`,
        method: "POST",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "likedArtists", id: userId },
      ],
      async onQueryStarted({ userId, artists }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getLikedArtists",
            { userId },
            (draft) => {
              draft.splice(0, 0, ...artists);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    removeFromLikedArtists: builder.mutation<
      boolean,
      { userId: string; body: { ids: string[] } }
    >({
      query: ({ userId, body }) => ({
        url: `/v1/User/${userId}/artists`,
        method: "DELETE",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "likedArtists", id: userId },
      ],
      async onQueryStarted(
        { userId, body: { ids } },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getLikedArtists",
            { userId },
            (draft) => {
              for (let idIndex = 0; idIndex < ids.length; idIndex++) {
                draft.splice(
                  draft.findIndex(
                    (likedArtist) => ids[idIndex] === likedArtist.id
                  ),
                  1
                );
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getLikedArtists: builder.query<Artist[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/User/${userId}/artists`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Artist>) => data.list,
      providesTags: (result, error, { userId }) => [
        { type: "likedArtists", id: userId },
      ],
    }),

    addToLikedPlaylists: builder.mutation<
      boolean,
      { userId: string; body: { ids: string[] }; playlists: Playlist[] }
    >({
      query: ({ userId, body }) => ({
        url: `/v1/User/${userId}/playlists`,
        method: "POST",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "likedPlaylists", id: userId },
      ],
      async onQueryStarted(
        { userId, playlists },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getLikedPlaylists",
            { userId },
            (draft) => {
              draft.splice(0, 0, ...playlists);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    removeFromLikedPlaylists: builder.mutation<
      boolean,
      { userId: string; body: { ids: string[] } }
    >({
      query: ({ userId, body }) => ({
        url: `/v1/User/${userId}/playlists`,
        method: "DELETE",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "likedPlaylists", id: userId },
      ],
      async onQueryStarted(
        { userId, body: { ids } },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getLikedPlaylists",
            { userId },
            (draft) => {
              for (let idIndex = 0; idIndex < ids.length; idIndex++) {
                draft.splice(
                  draft.findIndex(
                    (likedPlaylist) => ids[idIndex] === likedPlaylist.id
                  ),
                  1
                );
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getLikedPlaylists: builder.query<Playlist[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/User/${userId}/playlists`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Playlist>) => data.list,
      providesTags: (result, error, { userId }) => [
        { type: "likedPlaylists", id: userId },
      ],
    }),

    createPlaylist: builder.mutation<void, { name: string }>({
      query: (body) => ({
        url: `/v1/Playlist`,
        method: "POST",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["editablePlaylists"],
    }),
    editPlaylist: builder.mutation<
      void,
      { playlistId: string; body: Partial<Playlist> }
    >({
      query: ({ playlistId, body }) => ({
        url: `/v1/Playlist/${playlistId}`,
        method: "PUT",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["editablePlaylists"],
    }),
    removePlaylist: builder.mutation<void, { playlistId: string }>({
      query: ({ playlistId }) => ({
        url: `/v1/Playlist/${playlistId}`,
        method: "DELETE",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["editablePlaylists"],
    }),
    getOwnedPlaylists: builder.query<Playlist[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/Playlist?where[0][type]=equals&where[0][attribute]=createdById&where[0][value]=${userId}`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Playlist>) => data.list,
    }),
    getEditablePlaylists: builder.query<Playlist[], void>({
      query: () => ({
        url: `/v1/Playlist/user`,
        //url: `/v1/Playlist/user?orderBy=createdAt&order=desc`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Playlist>) => data.list,
      providesTags: ["editablePlaylists"],
    }),
    getEditablePlaylist: builder.query<Playlist, { playlistId: string }>({
      query: ({ playlistId }) => ({
        url: `/v1/Playlist/${playlistId}`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      providesTags: ["editablePlaylists"], // TODO on rename
    }),
    addToPlaylist: builder.mutation<
      boolean,
      { playlistId: string; body: { ids: string[] } }
    >({
      query: ({ playlistId, body }) => ({
        url: `/v1/Playlist/${playlistId}/tracks`,
        method: "POST",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["editablePlaylistTracks"], // TODO invalidate only one playlist tracks
    }),
    removeFromPlaylist: builder.mutation<
      boolean,
      { playlistId: string; body: { ids: string[] } }
    >({
      query: ({ playlistId, body }) => ({
        url: `/v1/Playlist/${playlistId}/tracks`,
        method: "DELETE",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["editablePlaylistTracks"], // TODO invalidate only one playlist tracks
    }),
    getEditablePlaylistTracks: builder.query<Track[], { playlistId: string }>({
      query: ({ playlistId }) => ({
        url: `/v1/Playlist/${playlistId}/tracks`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Track>) => data.list,
      providesTags: ["editablePlaylistTracks"],
    }),

    getProfile: builder.query<User, { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/social/profile/${userId}`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      providesTags: (result, error, { userId }) => [
        { type: "user", id: userId },
      ],
    }),
    requestFollow: builder.mutation<void, { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/social/follow/${userId}/request`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "user", id: userId },
      ],
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getProfile",
            { userId },
            (draft) => {
              draft.requestSent = true;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    cancelFollowRequest: builder.mutation<void, { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/social/follow/${userId}/rollback`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "user", id: userId },
      ],
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getProfile",
            { userId },
            (draft) => {
              draft.requestSent = false;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    acceptFollow: builder.mutation<void, { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/social/follow/${userId}/accepted`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["followRequests"],
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getFollowRequests",
            undefined,
            (draft) => {
              draft.splice(
                draft.findIndex((user) => user.id === userId),
                1
              );
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    rejectFollow: builder.mutation<void, { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/social/follow/${userId}/rejected`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["followRequests"],
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getFollowRequests",
            undefined,
            (draft) => {
              draft.splice(
                draft.findIndex((user) => user.id === userId),
                1
              );
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getFollowRequests: builder.query<User[], void>({
      query: () => ({
        url: `/v1/social/follow/requested`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<User>) => data.list,
      providesTags: ["followRequests"],
    }),
    unfollow: builder.mutation<
      void,
      { currentUserId: string; followingId: string }
    >({
      query: ({ followingId }) => ({
        url: `/v1/social/follow/${followingId}/unfollow`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { currentUserId, followingId }) => [
        "currentUser",
        { type: "user", id: currentUserId },
        { type: "user", id: followingId },
        { type: "friends", id: currentUserId },
        { type: "friends", id: followingId },
      ],
      async onQueryStarted(
        { currentUserId, followingId },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getFriends",
            { userId: currentUserId },
            (draft) => {
              draft.following.list.splice(
                draft.following.list.findIndex(
                  (following) => following.id === followingId
                ),
                1
              );
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getFriends: builder.query<
      { followers: Result<User>; following: Result<User> },
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `/v1/social/${userId}`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      providesTags: (result, error, { userId }) => [
        { type: "friends", id: userId },
      ],
    }),
    removeFollower: builder.mutation<
      void,
      { currentUserId: string; followerId: string }
    >({
      query: ({ followerId }) => ({
        url: `/v1/social/follow/${followerId}/remove`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { currentUserId, followerId }) => [
        "currentUser",
        { type: "user", id: currentUserId },
        { type: "user", id: followerId },
        { type: "friends", id: currentUserId },
        { type: "friends", id: followerId },
      ],
      async onQueryStarted(
        { currentUserId, followerId },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getFriends",
            { userId: currentUserId },
            (draft) => {
              draft.followers.list.splice(
                draft.followers.list.findIndex(
                  (follower) => follower.id === followerId
                ),
                1
              );
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    getAnnouncements: builder.query<Announcement[], void>({
      query: () => ({
        url: `/v1/news`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Announcement>) => data.list,
    }),
    markAnnouncementAsRead: builder.mutation<void, { announcementId: string }>({
      query: ({ announcementId }) => ({
        url: `/v1/News/${announcementId}/read`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["unreadNotificationsCount"],
    }),
    getUnreadNotificationsCount: builder.query<number, void>({
      query: () => ({
        url: `/v1/App/action/notification`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      providesTags: ["unreadNotificationsCount"],
      transformResponse: (data: { total: number }) => data.total,
    }),
    reportUser: builder.mutation<void, { userId: string; reason: string }>({
      query: ({ userId, reason }) => ({
        url: `/v1/UserReportRequest`,
        method: "POST",
        body: { description: reason, userReportedId: userId },
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
    }),
    getMatches: builder.query<Match[], void>({
      query: () => ({
        url: `/v1/MachMaker`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Match>) => data.list,
    }),
    setMatchLiked: builder.mutation<void, { matchId: string }>({
      query: ({ matchId }) => ({
        url: `/v1/MachMaker/${matchId}/like`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      async onQueryStarted({ matchId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getMatches",
            undefined,
            (draft) => {
              draft.splice(
                draft.findIndex((match) => match.id === matchId),
                1
              );
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    setMatchDisliked: builder.mutation<void, { matchId: string }>({
      query: ({ matchId }) => ({
        url: `/v1/MachMaker/${matchId}/rejected`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      async onQueryStarted({ matchId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          currentUserApiUtil.updateQueryData(
            "getMatches",
            undefined,
            (draft) => {
              draft.splice(
                draft.findIndex((match) => match.id === matchId),
                1
              );
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    createChatAndSendMessage: builder.mutation<
      Message,
      {
        currentUserId: string;
        contactId: string;
        post: string;
      }
    >({
      query: ({ currentUserId, contactId, post }) => ({
        url: `/v1/Chat/send`,
        method: "POST",
        body: {
          post,
          attachementsIds: [],
          parentId: "",
          usersIds: [currentUserId, contactId],
          type: "Post",
        },
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error) => [
        { type: "messages", id: result ? result.parentId : "" },
      ],
    }),
    sendMessage: builder.mutation<
      Message,
      {
        body: {
          post: string;
          attachmentsIds: string[];
          parentId: string;
        };
        createdById: string;
      }
    >({
      query: ({ body }) => ({
        url: `/v1/Chat/send`,
        method: "POST",
        body,
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: (result, error, { body: { parentId } }) => {
        if (result) {
          return [{ type: "messages", id: `${parentId}:${result.createdAt}` }];
        } else {
          return [];
        }
      },
    }),
    getChats: builder.query<Chat[], void>({
      query: () => ({
        url: `/v1/Chat`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Chat>) => data.list,
      providesTags: ["chats"],
    }),
    getChat: builder.query<Chat, { chatId: string }>({
      query: ({ chatId }) => ({
        url: `/v1/Chat/${chatId}`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
    }),
    getChatByUserId: builder.query<Chat, { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/Chat/user/${userId}/history`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
    }),
    clearChat: builder.mutation<void, { chatId: string }>({
      query: ({ chatId }) => ({
        url: `/v1/Chat/${chatId}/left`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      invalidatesTags: ["chats"],
    }),
    getMessages: builder.query<
      Result<Message>,
      { chatId: string; before: string; after: string }
    >({
      query: ({ chatId, after, before }) => ({
        url:
          `/v1/Chat/${chatId}/posts` +
          `?maxSize=${MESSAGE_PAGE_SIZE}&where[0][attribute]=createdAt` +
          `&where[0][type]=before&where[0][value]=${before}&where[0][dateTime]=true` +
          `&where[1][attribute]=createdAt` +
          `&where[1][type]=after&where[1][value]=${after}&where[1][dateTime]=true`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Message>) => {
        data.list.reverse();
        return data;
      },
      providesTags: (result, error, { chatId, after, before }) => [
        { type: "messages", id: `${chatId}:${after}:${before}` },
      ],
    }),
    getUnreadChatsCount: builder.query<number, void>({
      query: () => ({
        url: `/v1/Chat/action/notification`,
        method: "GET",
        headers: [["Arvano-Authorization", "placeholder"]],
      }),
      transformResponse: (data: Result<Chat>) => +data.total,
    }),
  }),
});

export const {
  useSendOtpMutation,
  useVerifyOtpMutation,
  useCreateUserMutation,
  useUploadFileMutation,
  useAssignAvatarMutation,
  useGetCurrentUserQuery,
  useLoginMutation,
  useGetPlansQuery,
  useGetUserGenresQuery,
  useSetUserGenresMutation,
  useRemoveFromUserGenresMutation,
  useEditUserMutation,
  useResetPasswordMutation,
  useEditPasswordMutation,
  useGetUserHomeGenresQuery,
  useAddToPlayedTracksMutation,
  useGetPlayedTracksQuery,
  useAddToLikedTracksMutation,
  useRemoveFromLikedTracksMutation,
  useGetLikedTracksQuery,
  useAddToLikedArtistsMutation,
  useRemoveFromLikedArtistsMutation,
  useGetLikedArtistsQuery,
  useGetOwnedPlaylistsQuery,
  useAddToLikedPlaylistsMutation,
  useRemoveFromLikedPlaylistsMutation,
  useGetLikedPlaylistsQuery,
  useCreatePlaylistMutation,
  useEditPlaylistMutation,
  useRemovePlaylistMutation,
  useGetEditablePlaylistsQuery,
  useGetEditablePlaylistQuery,
  useGetEditablePlaylistTracksQuery,
  useAddToPlaylistMutation,
  useRemoveFromPlaylistMutation,
  useGetProfileQuery,
  useRequestFollowMutation,
  useCancelFollowRequestMutation,
  useAcceptFollowMutation,
  useRejectFollowMutation,
  useGetFollowRequestsQuery,
  useUnfollowMutation,
  useGetFriendsQuery,
  useRemoveFollowerMutation,
  useGetAnnouncementsQuery,
  useMarkAnnouncementAsReadMutation,
  useGetUnreadNotificationsCountQuery,
  useReportUserMutation,
  useGetMatchesQuery,
  useSetMatchLikedMutation,
  useSetMatchDislikedMutation,
  useCreateChatAndSendMessageMutation,
  useSendMessageMutation,
  useGetChatsQuery,
  useGetChatQuery,
  useGetChatByUserIdQuery,
  useClearChatMutation,
  useGetMessagesQuery,
  useGetUnreadChatsCountQuery,
  util: currentUserApiUtil,
} = currentUserApi;
